import { IAddStudyData, ICollaboratorData } from "src/pages/Studies/models/interfaces";

export interface QuestionNode {
	key: string;
	title: string;
	question_id: number;
	question_name: string;
	isLeaf: boolean;
}

export interface TreeDataNode {
	key: string;
	title: string;
	sub_section_id: number;
	sub_section_name: string;
	children: QuestionNode[];
}

export interface SubSection {
	sub_section_id: number;
	sub_section_name: string;
	questions: {
		question_id: number;
		question_name: string;
	}[];
}

export interface SubSectionProps {
	index?: number;
	title: string;
	sequence?: number;
	placeholder: string;
	subSectionId: string | number;
	currentIndex: string | number;
	textValue: string;
	isQuestion: boolean;
	dataArray?: TreeDataNode[] | QuestionNode[];
}

export interface SubSectionListProps {
	payload: SubSection[];
	activeSectionId: number;
	handleAddQuestion: (index: number) => void;
	handleInputChange: (value: string, index: string) => void;
	handleDeleteSubSection: (index: string) => void;
	handleCopyContent: (index: string) => void;
	expanded: any;
	setExpanded: any;
	copiedSubSectionId: number | string;
	setCopiedSubSectionId: any;
	form: any;
	validateForm?: any;
}

export interface AddQuestionProps {
	subSectionId: number;
	handleAddQuestion: (subSectionId: number) => void;
}

export interface Section {
	section_id: number;
	section_name: String;
	sub_section: SubSection[];
}

export interface IRightSectionProps {
	handleClose: () => void;
	setIsEdit: any;
	isEdit: boolean;
	handleFinish: () => void;
	form: any;
	isOnline?: any;
	reconnectingRef?: any;
	handleRetry?: () => void;
	isSaving?: boolean;
	handleManualSave?: () => void;
	hasValidationError: boolean;
	lastSavedInfo?: {
		dgId: string | null;
		timestamp: string | null;
		isManualSave: boolean;
	};
	onValidationError?: (hasError: boolean) => void;
	isAutoSaving?: boolean;
	setIsAutoSaving?: (isAutoSaving: boolean) => void;
}

export interface IDGTitleProps {
	isSection: boolean;
	handleEditDescriptionGuide?: () => void;
	handlePreviewDescriptionGuide?: () => void;
}

export interface ManageDescriptionGuideState {
	sections: Section[];
	activeSectionId: number;
	edit: any;
	upload_details: any;
	dg_name: string;
	version_history: any;
}

export interface IDescriptionGuideProps {
	dg_id: string;
	study_id: string;
	handleClose: () => void;
	messageApi: any;
	handleCloseAfterRelease: () => void;
	onConfirm: () => void;
}

interface VersionHistory {
	created_dt: string;
	created_by: string;
	modified_dt: string | null;
	modified_by: string | null;
}

interface Geography {
	_id: string;
	drop_down_title: string;
	drop_down_type: string;
}

interface CustomEntityTag {
	_id: string;
	drop_down_title: string;
	drop_down_type: string;
}

interface Metadata {
	description: string;
	start_dt: string;
	end_dt: string;
	geography: Geography;
	custom_entity_tags: CustomEntityTag[];
}

interface UploadDetails {
	cloud_path: string;
	filename: string;
	upload_dt: string;
	uploaded_by: string;
}

interface Edit {
	edit_lock: string;
	edit_lock_by: string;
	edit_lock_start_time: string;
}

interface ProcessingHistory {
	[key: string]: string;
	created_dt: string;
	created_by: string;
}

interface DiscussionGuide {
	active_dg_id: string;
	upload_details: UploadDetails;
	edit: Edit;
	processing_status: string;
	processing_history: ProcessingHistory[];
}

interface Transcript {
	upload_details: UploadDetails;
	edit: Edit;
	tags: Array<{
		meta_id: string;
		tag_category: string;
		tag_value: string;
	}>;
	processing_status: string;
	active_transcript_id: string;
	processing_history: ProcessingHistory[];
}

interface Report {
	upload_details: UploadDetails;
	processing_status: string;
	processing_history: ProcessingHistory[];
	active_report_id: string;
	tags: Array<{
		meta_id: string;
		tag_category: string;
		tag_value: string;
	}>;
}

interface ProcessedElements {
	discussion_guide: DiscussionGuide[];
	transcripts: Transcript[];
	reports: Report[];
	taxonomy: null;
	note: any;
}

export interface ManageStudyState {
	access_Type: string;
	isEditable: boolean;
	manageStudyDetails: any;
	insights_id: string;
	isEditedTrans: any;
}

export enum speakerType {
	type1 = "Interviewer",
	type2 = "Respondent",
}

export const SpeakerType = [
	{ id: "1", name: "Interviewer" },
	{ id: "2", name: "Respondent" },
];

export interface IinsightsButtonStatusObj {
	button_state: boolean;
	text_on_button: string;
	processing_message: string;
	log_available?: boolean;
	insights_available?: boolean;
	active_insights_id?: string | null;
	progress_status?: {
		document_type: string;
		file_id: string;
		progress: number;
		status: string;
		processing_message: string;
		started_at: string;
		completed_at: string;
		error_message: string | null;
	};
}

export interface ItranscriptEntryProps {
	key: string;
	time: string;
	speaker: string;
	text: string;
	highlightedText: string;
	matchCase: boolean;
	focusOnMount?: boolean;
	isTranscriptEditable: boolean;
	index: any;
	indx: number;
	form: any;
	hasError?: boolean;
	isActive?: boolean;
	// AV edit mode props
	showEditPlayer?: boolean;
	isPlaying?: boolean;
	onSeekToDialog?: (seconds: number, dialogId: number) => void;
	onTextChange?: (dialogId: any, newText: any) => void;
	handleTranscriptSplitMerge?: (
		id1: number,
		text1: string,
		id2: number,
		text2: string,
		part1Timestamps?: { timestamp_start: number | null; timestamp_end: number | null },
		part2Timestamps?: { timestamp_start: number | null; timestamp_end: number | null },
	) => void;
	handleOtherChanges?: (dialogId: number, key: string, value: string) => void;
	onEditFocus?: () => void;
	searchQuery?: string;
	isAfterNarration?: boolean;
}

export const CHUNK_SIZE = 5 * 1024 * 1024;

export interface IfileDetails {
	name: string;
	file_size: number;
	totalChunks: number;
}

export interface IManageStudyProps {
	getStudyDetailData: () => void;
	handleClose: () => void;
	handleEditDescriptionGuide: () => void;
	handleEditTranscript: () => void;
	handleEditNotes: (data) => void;
	handlePreviewDescriptionGuide: () => void;
	handleViewNotes: () => void;
}

export interface IUIState {
	openDescriptionGuide: boolean;
	openTranscript: boolean;
	openNotes: boolean;
	openViewNotes: boolean;
	formKey?: number;
	loading: boolean;
	error: boolean;
	errorCode?: number | null;
}

export interface INoteTag {
	tag_category: string;
	tag_value: string;
}

export interface INote {
	notes_id: string;
	notes_text: string;
	date: string;
	tags: INoteTag[];
}

export interface IPagination {
	current: number;
	pageSize: number;
	total: number;
}

export interface IViewNotesProps {
	messageApi: any;
	handleClose: () => void;
	searchText: string;
	onSearchChange: (value: string) => void;
}

export interface IManageQuantProps {
	profile?: string;
	studyData?: IAddStudyData;
	payload?: ICollaboratorData[];
	studyId?: string;
}

export interface IManageStudiesProps {
	profile?: string;
	studyData?: IAddStudyData;
	payload?: ICollaboratorData[];
	studyId?: string;
}

export interface ITranscriptMediaData {
	media_url?: string;
	media_name?: string;
	video_presigned?: string[];
	summary?: string;
}
